import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

export interface CompanyInsight {
  insight: string;
  recommendedScriptType: string;
  weakestCompetency: string;
}

interface CompanyInsightsListProps {
  insights: CompanyInsight[];
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  selectedInsightIndex: number | null;
  onSelectInsight: (index: number, scriptType: string, isCompanyInsight: boolean) => void;
}

const CompanyInsightsList: React.FC<CompanyInsightsListProps> = ({
  insights,
  isLoading,
  isFetching,
  onRefresh,
  selectedInsightIndex,
  onSelectInsight,
}) => {
  const { isDark } = useDashboardTheme();
  if (isLoading || (isFetching && insights.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4} mb={4} sx={{ background: "var(--color-bg)", borderRadius: "16px", border: "1px solid var(--color-border)" }}>
        <CircularProgress size={24} sx={{ color: "var(--color-stat-accent)", mr: 2 }} />
        <Typography variant="body1" sx={{ color: "var(--color-text)", opacity: 0.8 }}>Analyzing company history...</Typography>
      </Box>
    );
  }

  if (!insights || insights.length === 0) {
    return null;
  }

  return (
    <Box mb={4} p={3} sx={{
      background: isDark ? "rgba(234,88,12,0.08)" : "rgba(234,88,12,0.04)",
      border: isDark ? "1px solid rgba(234,88,12,0.2)" : "1px solid rgba(234,88,12,0.15)",
      borderRadius: "16px",
      position: "relative"
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="overline" sx={{ color: isDark ? "#FDBA74" : "#C2410C", fontWeight: "bold", letterSpacing: 1.5 }}>
          AI Company Insights
        </Typography>
        <Button
          onClick={onRefresh}
          disabled={isFetching}
          startIcon={<RefreshIcon />}
          sx={{
            color: isDark ? "#FDBA74" : "#C2410C",
            textTransform: "none",
            fontSize: "0.8rem",
            opacity: 0.8,
            minWidth: "auto",
            "&:hover": { opacity: 1, backgroundColor: "rgba(234,88,12,0.1)" }
          }}
        >
          {isFetching ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: "var(--color-text)", opacity: 0.8, mb: 3 }}>
        Select a historical hurdle below to configure the script type.
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {insights.map((insight, index) => (
          <Box
            key={index}
            onClick={() => onSelectInsight(index, insight.recommendedScriptType, true)}
            sx={{
              p: 2,
              borderRadius: "12px",
              cursor: "pointer",
              border: selectedInsightIndex === index 
                ? `2px solid ${isDark ? "#FDBA74" : "#C2410C"}` 
                : "1px solid var(--color-border)",
              backgroundColor: selectedInsightIndex === index 
                ? "rgba(234,88,12,0.15)" 
                : "var(--color-surface)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: selectedInsightIndex === index ? (isDark ? "#FDBA74" : "#C2410C") : "var(--color-text)"
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem", color: "var(--color-text)" }}>
              Historical Hurdle: <span style={{ color: "var(--color-stat-accent)" }}>{insight.weakestCompetency}</span>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "var(--color-text)", opacity: 0.8, lineHeight: 1.6 }}>
              {insight.insight}
            </Typography>
            <Box mt={2} display="flex" gap={1}>
              <Typography variant="caption" sx={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: "12px",
                backgroundColor: "rgba(234,88,12,0.1)",
                color: isDark ? "#FDBA74" : "#C2410C",
                fontWeight: 600
              }}>
                Recommended format: {insight.recommendedScriptType}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CompanyInsightsList;
